import { useLazyQuery, useMutation } from "@apollo/client";
import { parsePhoneNumber } from "awesome-phonenumber";
import cn from "classnames";
import { setCookie } from "cookies-next";
import { Formik } from "formik";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { useState, useEffect, useRef, createRef } from "react";
import { toast } from "react-toastify";
import * as Yup from "yup";

import {
  VERIFY_EMAIL_CODE,
  SEND_EMAIL,
  PROFILE_EXISTS,
} from "@api/mutations/sign-up";
import { SIGN_UP } from "@api/mutationsV2/profile";
import { GET_PROFILE } from "@api/queriesV2/profile";
import RegistrationProfileFields from "@components/RegistrationProfileFields/RegistrationProfileFields";
import { validToUseExponea } from "@config/config";
import { useIsMounted } from "@hooks/useIsMounted";
import { useStore } from "@hooks/useZustandProvider";
import registrationSvg from "@images/svg/registration.svg";
import SocialSignIn from "@modals/Login/components/SocialSignIn";
import i from "@styles/icons.module.scss";
import Button from "@ui-kit/Button/Button";
import Editor from "@ui-kit/cms/Editor/Editor";
import Container from "@ui-kit/Container/Container";
import HiddenInput from "@ui-kit/HiddenInput/HiddenInput";
import Notice from "@ui-kit/Notice/Notice";
import PhoneInput from "@ui-kit/PhoneInput/PhoneInput";
import TicketsInput from "@ui-kit/TicketsInput/TicketsInput";
import { isValidBirthDate } from "@utils/birthDate";
import { toApiPhoneAreaCode } from "@utils/phone";
import { getSignupInput } from "@utils/registration";
import { regExpForEmail, regExpForNames } from "@utils/utils";

import s from "./RegistrationBlock.module.scss";

const RegistrationBlock = ({ data, settings, updateField }) => {
  const isMounted = useIsMounted();
  const { title } = data;
  const router = useRouter();
  const { t } = useTranslation();
  const { user, updateProfile } = useStore();
  const [error, setError] = useState("");
  const { phone } = user;

  const [isProfileExists, { loading: profileExistsLoading }] = useMutation(
    PROFILE_EXISTS,
    {
      onError: (error) => {
        toast.error(`[${error.name}]: ${error.message}`);
      },
    },
  );

  const [getProfile, { loading: profileLoading }] = useLazyQuery(GET_PROFILE, {
    fetchPolicy: "network-only",
    context: { clientName: "v2" },
    onCompleted: (data) => {
      if (data.profile) {
        try {
          const profile = data.profile;
          const PhoneAreaCode = toApiPhoneAreaCode(profile.PhoneAreaCode);
          const fullNumber = `${PhoneAreaCode}${profile.PhoneNumber}`;
          const countryCode = parsePhoneNumber(fullNumber).regionCode;

          if (validToUseExponea()) {
            exponea.identify({ registered: profile.email });
            exponea.update({
              email: profile.email,
              phone_number: fullNumber,
              first_name: profile.firstName,
              last_name: profile.lastName,
            });
            exponea.track("customer_registration", {
              email: profile.email,
              phone: profile.PhoneNumber,
              area_code: profile.PhoneAreaCode,
              country: countryCode,
              registered: true,
            });
          }

          if (window.dataLayer) {
            dataLayer.push({
              event: "registration",
              email: profile.email,
              phone: profile.PhoneNumber,
              area_code: profile.PhoneAreaCode,
              country: countryCode,
            });
          }

          updateProfile({
            ...profile,
            PhoneAreaCode: (profile.PhoneAreaCode || "").replace("+", ""),
          });

          router.push("/profile/personal-info/");
        } catch (e) {
          console.error("Error in profile processing:", e);
        }
      }
    },
    onError: (error) => {
      toast.error(`[${error.name}]: ${error.message}`);
    },
  });

  const [sendEmail, { data: sendEmailData, loading: sendEmailLoading }] =
    useMutation(SEND_EMAIL);

  const [
    signUp,
    { loading: signUpLoading, data: signUpData = {}, called: signUpCalled },
  ] = useMutation(SIGN_UP, {
    context: { clientName: "v2" },
    onError: (error) => {
      toast.error(`[${error.name}]: ${error.message}`);
    },
  });

  const [
    verifyEmailCode,
    { data: verifyEmailCodeData, loading, called: calledVerifyEmailCode },
  ] = useMutation(VERIFY_EMAIL_CODE, {
    onCompleted: (data) => {
      const errors = data?.verifyEmailCode?.errors;

      if (errors?.length) {
        setError(`${errors[0].messages[0]}`);
        toast.error(`${errors[0].messages[0]}`);
        return;
      }

      setError("");
      calledVerifyEmailCode && nextStep();
    },
    onError: (error) => {
      toast.error(`[${error.name}]: ${error.message}`);
    },
  });

  const [step, setStep] = useState(1);
  const resetCode = {
    1: "",
    2: "",
    3: "",
    4: "",
  };
  const [code, setCode] = useState(resetCode);
  const codeInput1 = useRef();
  const codeInput2 = useRef();
  const codeInput3 = useRef();
  const codeInput4 = useRef();
  const phoneNumberInput = createRef();
  const inputsRefs = {
    1: codeInput1,
    2: codeInput2,
    3: codeInput3,
    4: codeInput4,
  };

  const nextStep = () => {
    setStep(step + 1);
  };

  const prevStep = () => {
    setError("");
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleChangeCode = (e) => {
    const inputNumber = +e.target.dataset.number;
    const { value = "" } = e.target;
    if (value.length === 1) {
      setCode((code) => ({
        ...code,
        [inputNumber]: value,
      }));
      if (inputsRefs[inputNumber + 1]?.current) {
        inputsRefs[inputNumber + 1].current.focus();
        inputsRefs[inputNumber + 1].current.select();
      }
    } else if (value.length === 0) {
      setCode((code) => ({
        ...code,
        [inputNumber]: value,
      }));
    }
  };

  const handleFocus = (e) => {
    const inputNumber = +e.target.dataset.number;
    if (inputsRefs[inputNumber]?.current) {
      inputsRefs[inputNumber].current.select();
    }
  };

  useEffect(() => {
    if (step === 2) {
      setCode(resetCode);
    }
  }, [step]);

  useEffect(() => {
    if (!isMounted) return;

    const token = signUpData?.signup?.token;
    const errors = signUpData?.signup?.field;

    if (errors) {
      toast.error(`[${errors}]: ${signUpData?.signup?.messages?.[0] || ""}`);
      return;
    }

    if (!signUpLoading && signUpCalled) {
      if (token) {
        setCookie("token", token);
        getProfile();
      }
    }
  }, [signUpLoading, signUpCalled]);

  const handleSignUp = async (values) => {
    try {
      await signUp({
        variables: {
          input: getSignupInput(values),
        },
      }).catch((error) => {
        console.error("Sign up error:", error);
        toast.error(
          t("An error occurred during registration. Please try again."),
        );
      });
    } catch (error) {
      console.error("Error in handleSignUp:", error);
      toast.error(
        t("An error occurred during registration. Please try again."),
      );
    }
  };

  return (
    <Container className={cn(s.registration)}>
      <Container full={false}>
        <section>
          <div className={s.image}>
            <Image src={registrationSvg} width={422} height={357} alt="" />
          </div>
          <Formik
            // innerRef={formRef}
            validateOnMount
            enableReinitialize
            onSubmit={handleSignUp}
            initialValues={{
              phoneNumber: phone,
              firstName: "",
              lastName: "",
              email: "",
              password: "",
              confirmPassword: "",
              confirmPrivacyPolicy: false,
              country: "",
              city: "",
              gender: "",
              birthDate: "",
            }}
            validationSchema={Yup.object().shape({
              birthDate: Yup.string().test(
                "birthDate",
                t("registration.invalid birth date"),
                isValidBirthDate,
              ),
              phoneNumber: Yup.string()
                .max(
                  20,
                  t("validation.error.max", {
                    field: t("phone number"),
                    max: 20,
                  }),
                )
                .min(
                  10,
                  t("validation.error.min", {
                    field: t("phone number"),
                    min: 10,
                  }),
                )
                .required(
                  t("validation.error.require", {
                    field: t("phone number"),
                  }),
                ),
              email: Yup.string()
                .email(
                  t("validation.error.email", {
                    field: t("email"),
                  }),
                )
                .matches(
                  regExpForEmail,
                  t("validation.error.email", {
                    field: t("email"),
                  }),
                )
                .required(
                  t("validation.error.require", {
                    field: t("email"),
                  }),
                ),
              firstName: Yup.string()
                .required(
                  t("validation.error.require", {
                    field: t("first name"),
                  }),
                )
                .matches(
                  regExpForNames,
                  t("validation.error.names", {
                    field: t("first name"),
                  }),
                ),
              lastName: Yup.string()
                .required(
                  t("validation.error.require", {
                    field: t("last name"),
                  }),
                )
                .matches(
                  regExpForNames,
                  t("validation.error.names", {
                    field: t("last name"),
                  }),
                ),
              password: Yup.string()
                .required(
                  t("validation.error.require", {
                    field: t("password"),
                  }),
                )
                .min(
                  6,
                  t("validation.error.min", {
                    field: t("password"),
                    min: 6,
                  }),
                ),
              confirmPassword: Yup.string()
                .required(
                  t("validation.error.require", {
                    field: t("confirm password"),
                  }),
                )
                .oneOf([Yup.ref("password"), null], t("passwords must match")),
              confirmPrivacyPolicy: Yup.boolean()
                .required(
                  t("validation.error.require", {
                    field: t("privacy policy"),
                  }),
                )
                .oneOf(
                  [true],
                  t("validation.error.require", {
                    field: t("privacy policy"),
                  }),
                ),
            })}
          >
            {(props) => {
              const {
                values,
                touched,
                errors,
                handleChange,
                handleBlur,
                setFieldValue,
                setFieldError,
                handleSubmit,
                isSubmitting,
                isValid,
              } = props;
              const isRegistering =
                isSubmitting || signUpLoading || profileLoading;

              return (
                <form
                  className={s.form}
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (step === 5 && !isRegistering) {
                      handleSubmit(event);
                    }
                  }}
                >
                  {step === 1 && (
                    <div className={cn(s.step, s.step1)}>
                      {/* <h2 className={s.title}>{t("registration")}</h2> */}

                      {/* <h1>
                        <Editor field={{ title }} onChange={updateField} onlyText />
                      </h1> */}
                      <h2>
                        <Editor
                          field={{ title }}
                          onChange={updateField}
                          onlyText
                        />
                        {/* <BlockTitle
                          field={{ title }}
                          onChange={updateField}
                          type={blockType}
                        /> */}
                      </h2>
                      <h5>{t("Email and Phone")}</h5>
                      <p className={s.text}>
                        {t("Please, indicate your Email and Phone number")}
                      </p>
                      <div className={s.input}>
                        <TicketsInput
                          // autoFocus
                          name="email"
                          label={t("email")}
                          type="text"
                          required
                          value={values.email}
                          onChange={(e) => {
                            e.target.value = e.target.value.trim();
                            handleChange(e);
                          }}
                          onBlur={handleBlur}
                          errors={touched.email && errors.email}
                        />
                      </div>
                      <div className={s.input}>
                        <PhoneInput
                          // autoFocus
                          ref={phoneNumberInput}
                          inputClassName={s.phone}
                          name="phoneNumber"
                          label={t("phone number")}
                          placeholder={t("enter phone number")}
                          value={values.phoneNumber}
                          required
                          onChange={(e) => {
                            setFieldValue("phoneNumber", e);
                          }}
                          onBlur={handleBlur}
                          errors={touched.phoneNumber && errors.phoneNumber}
                        />
                      </div>
                      {!!sendEmailData?.sendEmail?.errors?.length && (
                        <Notice
                          color="red"
                          className={s.error}
                          text={sendEmailData.sendEmail.errors[0].messages[0]}
                        />
                      )}
                      <div className={s.controls}>
                        <Button
                          disabled={
                            sendEmailLoading ||
                            profileExistsLoading ||
                            errors.phoneNumber ||
                            errors.email
                          }
                          className={s.button}
                          text={
                            sendEmailLoading || profileExistsLoading
                              ? t("loading")
                              : t("continue")
                          }
                          onClick={() => {
                            isProfileExists({
                              variables: {
                                email: values.email,
                              },
                            }).then((data) => {
                              if (data?.data?.profileExists?.error) {
                                setFieldError(
                                  "email",
                                  data?.data?.profileExists?.error.ErorReason,
                                );
                                return;
                              }
                              if (data?.data?.profileExists?.exists) {
                                setFieldError(
                                  "email",
                                  t("this email already exists"),
                                );
                              } else {
                                sendEmail({
                                  variables: {
                                    input: {
                                      email: values.email,
                                    },
                                  },
                                }).then((data) => {
                                  if (!data.data.sendEmail.errors.length) {
                                    nextStep();
                                  }
                                });
                              }
                            });
                          }}
                        />
                      </div>
                      <SocialSignIn
                        className={s.social}
                        onSuccess={() => router.push("/profile/personal-info/")}
                      >
                        <p className={s.terms}>
                          {t("i read and agree with Lux Express")}
                          <Link href="/privacy-policy/" className={s.link}>
                            {t("privacy policy")}.
                          </Link>
                        </p>
                      </SocialSignIn>
                    </div>
                  )}
                  {step === 2 && (
                    <div className={cn(s.step, s.step2)}>
                      <h2 className={s.title}>{t("registration")}</h2>
                      <h5>{t("email verification")}</h5>
                      <p className={s.text}>
                        {t("enter the code send to")}:{" "}
                        <span>{values.email}</span>
                      </p>
                      <div className={s.code}>
                        <div className={s.input}>
                          <TicketsInput
                            autoComplete="on"
                            autoFocus
                            inputRef={codeInput1}
                            name="code-1"
                            tabIndex={-1}
                            data-number={1}
                            placeholder="—"
                            type="tel"
                            required
                            className={s.codeInput}
                            value={code[1]}
                            // onKeyPress={onKeyPress}
                            // onClick={handleFocus}
                            onChange={handleChangeCode}
                            onFocus={handleFocus}
                          />
                        </div>
                        <div className={s.input}>
                          <TicketsInput
                            autoComplete="on"
                            inputRef={codeInput2}
                            name="code-2"
                            tabIndex={-2}
                            data-number={2}
                            placeholder="—"
                            type="tel"
                            required
                            className={s.codeInput}
                            value={code[2]}
                            // onKeyPress={onKeyPress}
                            // onClick={handleFocus}
                            onChange={handleChangeCode}
                            onFocus={handleFocus}
                          />
                        </div>
                        <div className={s.input}>
                          <TicketsInput
                            autoComplete="on"
                            inputRef={codeInput3}
                            name="code-3"
                            tabIndex={-3}
                            data-number={3}
                            placeholder="—"
                            type="tel"
                            required
                            className={s.codeInput}
                            value={code[3]}
                            // onKeyPress={onKeyPress}
                            // onClick={handleFocus}
                            onChange={handleChangeCode}
                            onFocus={handleFocus}
                          />
                        </div>
                        <div className={s.input}>
                          <TicketsInput
                            autoComplete="on"
                            inputRef={codeInput4}
                            name="code-4"
                            tabIndex={-4}
                            data-number={4}
                            placeholder="—"
                            type="tel"
                            required
                            className={s.codeInput}
                            value={code[4]}
                            // onKeyPress={onKeyPress}
                            // onClick={handleFocus}
                            onChange={handleChangeCode}
                            onFocus={handleFocus}
                          />
                        </div>
                      </div>
                      {!!error && <Notice color="red" text={error} />}
                      <div className={s.controls}>
                        <div onClick={prevStep} className={s.back}>
                          <icon className={i.arrowLeft} />
                          {t("back")}
                        </div>
                        <Button
                          className={s.button}
                          text={t("send code")}
                          disabled={
                            Object.values(code).join("").length !== 4 || loading
                          }
                          onClick={() =>
                            verifyEmailCode({
                              variables: {
                                input: {
                                  email: values.email,
                                  code: Object.values(code).join(""),
                                },
                              },
                            })
                          }
                        />
                      </div>
                    </div>
                  )}
                  {step === 3 && (
                    <div className={cn(s.step, s.step3)}>
                      <h2 className={s.title}>{t("registration")}</h2>
                      <h5>{t("First and Last name")}</h5>
                      <p className={s.text}>
                        {t("please, enter your first and last name")}
                      </p>
                      <div className={s.input}>
                        <TicketsInput
                          autoFocus
                          name="firstName"
                          type="text"
                          required
                          label={t("first name")}
                          value={values.firstName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          errors={touched.firstName && errors.firstName}
                        />
                      </div>
                      <div className={s.input}>
                        <TicketsInput
                          name="lastName"
                          type="text"
                          label={t("last name")}
                          required
                          value={values.lastName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          errors={touched.lastName && errors.lastName}
                        />
                      </div>
                      <div className={s.controls}>
                        <div onClick={() => setStep(1)} className={s.back}>
                          <icon className={i.arrowLeft} />
                          {t("back")}
                        </div>
                        <Button
                          disabled={
                            errors.firstName || errors.lastName || errors.email
                          }
                          className={s.button}
                          text={t("continue")}
                          onClick={nextStep}
                        />
                      </div>
                    </div>
                  )}
                  {step === 4 && (
                    <div className={cn(s.step, s.step4)}>
                      <h2 className={s.title}>{t("registration")}</h2>
                      <h5>{t("pick your password")}</h5>
                      <p className={s.text}>
                        {t("it must consist at least 6 characters")}
                      </p>
                      <div className={s.input}>
                        <HiddenInput
                          autoFocus
                          name="password"
                          placeholder="••••••"
                          label={t("password")}
                          required
                          value={values.password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          errors={touched.password && errors.password}
                        />
                      </div>
                      <div className={s.input}>
                        <HiddenInput
                          name="confirmPassword"
                          placeholder="••••••"
                          label={t("confirm password")}
                          required
                          value={values.confirmPassword}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          errors={
                            touched.confirmPassword && errors.confirmPassword
                          }
                        />
                      </div>
                      <div className={s.input}>
                        <div className={s.checkbox}>
                          <div
                            className={cn(
                              s.check,
                              values.confirmPrivacyPolicy && s.active,
                              errors.confirmPrivacyPolicy && s.checkError,
                            )}
                            onClick={() =>
                              setFieldValue(
                                "confirmPrivacyPolicy",
                                !values.confirmPrivacyPolicy,
                              )
                            }
                          />
                          {t("i read and agree with Lux Express")}
                          <Link href="/privacy-policy/" className={s.link}>
                            {t("privacy policy")}.
                          </Link>
                        </div>
                      </div>
                      <div className={s.controls}>
                        <div onClick={prevStep} className={s.back}>
                          <icon className={i.arrowLeft} />
                          {t("back")}
                        </div>
                        <Button
                          disabled={
                            errors.password ||
                            errors.confirmPassword ||
                            !values.confirmPrivacyPolicy ||
                            profileLoading ||
                            signUpLoading
                          }
                          className={s.button}
                          text={t("continue")}
                          onClick={nextStep}
                        />
                      </div>
                    </div>
                  )}
                  {step === 5 && (
                    <div className={s.step}>
                      <h2 className={s.title}>{t("registration")}</h2>
                      <h5>{t("personal info")}</h5>
                      <p className={s.text}>
                        {t("registration.optional details")}
                      </p>
                      <RegistrationProfileFields disabled={isRegistering} />
                      <div className={s.controls}>
                        <button
                          type="button"
                          onClick={prevStep}
                          className={s.back}
                          disabled={isRegistering}
                        >
                          <icon className={i.arrowLeft} />
                          {t("back")}
                        </button>
                        <Button
                          type="submit"
                          disabled={!isValid || isRegistering}
                          className={s.button}
                          text={isRegistering ? t("loading") : t("register")}
                        />
                      </div>
                    </div>
                  )}
                </form>
              );
            }}
          </Formik>
        </section>
      </Container>
    </Container>
  );
};

export default RegistrationBlock;
